import { flatmap } from '../../../flatmap.js';
import { mergeViewSchema } from '../../../mergeViewSchema/mergeViewSchema.js';
import { getExtension } from '../../../../string/getExtension.js';

/**
 * Devuelve la función que recupera el viewField a incluir
 * @param {Object} files Lista de dependencias {filename: file}
 * @return {function}
 */
export function includesFactory(files) {
  const indexedByFieldName = new Map(/*
    fieldName: {
      viewName: {
        viewName,
        ieceName
      }
    }
  */);

  const views = {
    /* viewName: view */
  };

  const ieces = {
    /* ieceName: iece */
  };

  Object.entries(files).forEach(([filename, file]) => {
    const name = filename.toUpperCase();
    const ext = getExtension(filename); // getExtension return in lower case
    if (ext === 'in4') {
      ieces[name] = file;
    } else if (ext === 'view') {
      views[name] = file;
      flatmap(file, viewField => {
        if (viewField.name) {
          const viewName = name;
          const { metadata: { include: { iece = '' } = {} } = {} } = viewField;
          const ieceName = iece.toUpperCase();

          if (!indexedByFieldName.has(viewField.name)) {
            indexedByFieldName.set(viewField.name, new Map());
          }
          const viewsMap = indexedByFieldName.get(viewField.name);
          viewsMap.set(viewName, { viewName, ieceName });
        }

        return viewField;
      });
    }
  });

  const validateParams = validateParamsFactory(indexedByFieldName, ieces);
  const getViewField = getViewFieldFactory();
  const getView = getViewFactory(views, ieces);

  /**
   * Devuelve el viewField a incluir.
   * @param {object} viewField el campo padre, tipo "include", de donde recuperamos:
   * - fieldName de viewField.metadata.name o viewField.name
   * - viewName (opcional) de viewField.metadata.view
   * - ieceName (opcional) de viewField.metadata.iece
   * @return {object} viewField el campo de la dependencia
   */
  return function get(viewField) {
    const metadata = viewField.metadata || {};
    const fieldName = metadata.name || viewField.name || '';

    const { viewName, ieceName } = validateParams(fieldName, metadata.view, metadata.iece);
    const view = getView(viewName, ieceName);
    return getViewField(fieldName, viewName, ieceName, view);
  };
}

function validateParamsFactory(indexedByFieldName, ieces) {
  /**
   * Busca y devuelve el nombre de view y opcionalmente la in4 a mergear.
   * Es que pueden venir definidos en el campo include del view padre en metadata,
   * o buscamos el view de dependencia por el nombre del campo.
   * Y en el view de dependencia puede venir el nombre de la in4,
   * o buscamos si hay una in4 que se llama igual en las dependencias.
   *
   * @param {string} fieldName el nombre del campo del view padre (el tipo include)
   * @param {string} [viewName] el nombre del view a mergear. Opcional, si no esta, lo buscamos
   * @param {string} [ieceName] el nombre de la in4 a mergear. Opcional, si no esta, lo buscamos
   */
  return function validateParams(fieldName, viewName = '', ieceName = '') {
    viewName = viewName.toUpperCase();
    ieceName = ieceName.toUpperCase();

    if (!indexedByFieldName.has(fieldName)) {
      throw new Error(`merge de dependencias. Ojo, No hemos encontrado el campo con name="${fieldName}" en las dependencias.\nRevisa que no falte una dependencia.`);
    }

    const fieldViews = indexedByFieldName.get(fieldName);

    if (!viewName && fieldViews.size > 1) {
      console.warn(
        `merge de dependencias. Ojo, el campo "${fieldName}" existe en varios views: ${Object.keys(fields).join(
          ', '
        )}\nDeberías especificar el view. Mientras se cojera el primero.`
      );
    }
    const fieldView = viewName ? fieldViews.get(viewName) : fieldViews.values().next().value;
    viewName = viewName || fieldView.viewName;
    ieceName = ieceName || fieldView.ieceName || '';

    if (!ieceName) {
      // OK, el nombre de la iece no esta indicado en ninguna parte.
      // ni el view padre `metadata.iece`, ni el view de dependencia `fieldView.ieceName`
      // pero vamos a intentar buscar la in4 (con el mismo nombre) en las dependencias
      ieceName = findIeceNameInDependencies(viewName, ieces);
    }

    return { viewName, ieceName };
  };
}

/**
 * Devuelve el nombre de la iece de las dependencias si es igual que el nombre del view.
 * Es para cuando no se especifica la iece en ningún view,
 * intentamos buscar a ver si hay una IN4 que se llama igual en las dependencias.
 *
 * @param {string} viewName El nombre del view a mergear (la dependencia)
 * @param {object} ieces Lista de ieces
 */
function findIeceNameInDependencies(viewName = '', ieces = {}) {
  const ieceName = viewName.replace('.VIEW', '.IN4');
  if (ieces[ieceName]) {
    // existe el objeto en las dependencias, registramos su nombre
    return ieceName;
  }
  return ''; // no existe, no hemos encontrado
}

function getViewFieldFactory() {
  const cache = {};

  return function getViewField(fieldName, viewName, ieceName, view) {
    if (!cache[viewName + ieceName]) {
      cache[viewName + ieceName] = createIndexed(view);
    }
    return cache[viewName + ieceName][fieldName];
  };

  function createIndexed(view) {
    const indexed = {};
    flatmap(view, viewField => {
      if (viewField.name) {
        indexed[viewField.name] = viewField;
      }
      return viewField;
    });

    return indexed;
  }
}

function getViewFactory(views, ieces) {
  const cache = {};

  return function getView(viewName, ieceName = '') {
    if (!cache[viewName + ieceName]) {
      if (ieceName) {
        cache[viewName + ieceName] = mergeViewSchema(views[viewName], ieces[ieceName]);
      } else {
        // no hay iece: cacheamos el view tal cual
        cache[viewName] = views[viewName];
      }
    }

    return cache[viewName + ieceName];
  };
}
